import React, { Component } from "react";
import Breadcrumb from "./common/breadcrumb";
import CountUp from "react-countup";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import {
  ShoppingCart,
  TrendingUp,
  Clock,
  Package,
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  Phone,
  MapPin,
} from "react-feather";
import { getAllProductStatsRedux } from "../actions";
import { getRecentOrders, getSingleProduct } from "../firebase/firebase.utils";
import { Sock, Tee } from "./common/garments";
import EmptyState from "./common/EmptyState";
import "./dashboard.css";

// Each stage gets a playroom tone (see .kd-tone--* in dashboard.css).
const PIPELINE = [
  { key: "Processing",        label: "Processing",     tone: "lilac",  link: "/orders/Processing" },
  { key: "Confirmed",         label: "Confirmed",      tone: "butter", link: "/orders/Confirmed" },
  { key: "Packing",           label: "Packing",        tone: "peach",  link: "/orders/Packing" },
  { key: "On the way",        label: "On the way",     tone: "sky",    link: "/orders/On the way" },
  { key: "Delivered",         label: "Delivered",      tone: "mint",   link: "/orders/Delivered" },
  { key: "Partial Delivered", label: "Part delivered", tone: "butter", link: "/orders/Partial Delivered" },
  { key: "Cancelled",         label: "Cancelled",      tone: "rose",   link: "/orders/Cancelled" },
];

// Hang-tag drop: string length from the sagging line to each tag (px).
const TAG_DROPS = [30, 18, 18, 30];

const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

const SkeletonRows = ({ count = 4 }) => (
  <div className="kd-skeleton" aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="kd-skeleton-row">
        <span className="kd-skeleton-box" />
        <span className="kd-skeleton-lines">
          <span />
          <span />
        </span>
      </div>
    ))}
  </div>
);

const EmptyLine = ({ children }) => <EmptyState>{children}</EmptyState>;


function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

// Local-midnight start/end of today (offset 0) or yesterday (offset 1).
function getDayRange(offset) {
  const start = new Date();
  start.setDate(start.getDate() - offset);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

const DAYS = [
  { key: "today",     label: "Today",     offset: 0 },
  { key: "yesterday", label: "Yesterday", offset: 1 },
];

function getInventory(p) {
  if (p.displayedVariations && p.displayedVariations.length > 0) {
    return p.displayedVariations.reduce((s, v) => s + (parseInt(v.inventory) || 0), 0);
  }
  return parseInt(p.inventory) || 0;
}

class Dashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedDay: "today",
      ordersByDay: {}, // dayKey → orders; each day is fetched once
      recentLoading: true,
      enriched: [],
      enrichedLoading: true,
    };
  }

  async componentDidMount() {
    const { productStats, getAllProductStatsRedux: loadStats } = this.props;
    if (loadStats && (!productStats || productStats.length === 0)) {
      loadStats();
    }
    await this.loadDay("today");
    await this.enrichStats();
  }

  loadDay = async (dayKey) => {
    if (this.state.ordersByDay[dayKey]) return;
    this.setState({ recentLoading: true });
    const { start, end } = getDayRange(DAYS.find((d) => d.key === dayKey).offset);
    const orders = await getRecentOrders(500, start.getTime(), end.getTime());
    this.setState((prev) => ({
      ordersByDay: { ...prev.ordersByDay, [dayKey]: orders },
      recentLoading: prev.selectedDay === dayKey ? false : prev.recentLoading,
    }));
  };

  selectDay = (dayKey) => {
    this.setState({
      selectedDay: dayKey,
      recentLoading: !this.state.ordersByDay[dayKey],
    });
    this.loadDay(dayKey);
  };

  enrichStats = async () => {
    const { productStats } = this.props;
    if (!productStats || productStats.length === 0) {
      this.setState({ enrichedLoading: false });
      return;
    }
    const enrichedWithDeleted = await Promise.all(
      productStats.slice(0, 10).map(async (stat) => {
        try {
          const product = await getSingleProduct(stat.productId);
          if (!product) return { ...stat, name: "Deleted product", inventory: 0, pictures: [], deleted: true };
          const inventory = product.displayedVariations && product.displayedVariations.length > 0
            ? product.displayedVariations.reduce((sum, v) => sum + (parseInt(v.inventory) || 0), 0)
            : parseInt(product.inventory) || 0;
          return { ...stat, name: product.name, pictures: product.pictures || [], inventory, productSlug: product.id };
        } catch {
          return { ...stat, name: "Unknown", inventory: 0, pictures: [] };
        }
      })
    );
    // Deleted products still have historical sales stats, but shouldn't
    // linger in the UI once the product itself is gone.
    const enriched = enrichedWithDeleted.filter((p) => !p.deleted);
    this.setState({ enriched, enrichedLoading: false });
  };

  getDeliveryAddress(order) {
    return order.deliveryAddress || null;
  }

  getOrderTotal(order) {
    return order.totalBill || 0;
  }

  getOrderPaid(order) {
    return order.totalPaid || 0;
  }

  getTotalRevenue() {
    const { allPayments } = this.props;
    if (!allPayments) return 0;
    return allPayments.reduce((acc, p) => {
      if (p.payments) {
        p.payments.forEach((pay) => { acc += parseInt(pay.amount) || 0; });
      }
      return acc;
    }, 0);
  }

  render() {
    const { allProducts, productStats, currentAdmin } = this.props;
    const { selectedDay, ordersByDay, recentLoading, enriched, enrichedLoading } = this.state;
    const dayOrders = ordersByDay[selectedDay] || [];
    const dayWord = selectedDay === "today" ? "today" : "yesterday";
    const dayTitle = selectedDay === "today" ? "Today's" : "Yesterday's";

    const recentOrders = dayOrders.slice(0, 5);
    const products = allProducts || [];

    const dayTotal      = dayOrders.length;
    const processingDay = dayOrders.filter((o) => o.orderStatus === "Processing").length;
    const deliveredDay  = dayOrders.filter((o) => o.orderStatus === "Delivered").length;
    const dayBill       = dayOrders.reduce((s, o) => s + this.getOrderTotal(o), 0);
    const dayPaid       = dayOrders.reduce((s, o) => s + this.getOrderPaid(o), 0);
    const dayDue        = dayOrders.reduce((s, o) => s + Math.max(0, this.getOrderTotal(o) - this.getOrderPaid(o)), 0);

    const outOfStock      = products.filter((p) => getInventory(p) === 0);
    const lowStock        = products.filter((p) => { const inv = getInventory(p); return inv > 0 && inv <= 5; });

    const pipeline = PIPELINE.map((stage) => ({
      ...stage,
      count: dayOrders.filter((o) => o.orderStatus === stage.key).length,
    }));

    const alertItems = [...outOfStock.slice(0, 4), ...lowStock.slice(0, 4)].slice(0, 8);
    const LOW_STOCK_THRESHOLD = 5;
    const topSold = enriched.length > 0 ? Math.max(...enriched.map((p) => p.totalSold || 0)) : 0;

    const adminName = currentAdmin ? currentAdmin.name || "Admin" : "Admin";
    const selectedDate = getDayRange(DAYS.find((d) => d.key === selectedDay).offset).start.toLocaleDateString("en-US", {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    });

    const tags = [
      {
        label: "Orders",
        value: dayTotal,
        prefix: "",
        sub: `${processingDay} processing, ${deliveredDay} delivered`,
        icon: <ShoppingCart size={18} />,
        tone: "sky",
      },
      {
        label: "Total bill",
        value: dayBill,
        prefix: "৳ ",
        sub: `Order value ${dayWord}`,
        icon: <TrendingUp size={18} />,
        tone: "butter",
      },
      {
        label: "Still due",
        value: dayDue,
        prefix: "৳ ",
        sub: `Outstanding from ${dayWord}`,
        icon: <Clock size={18} />,
        tone: "peach",
      },
      {
        label: "Paid",
        value: dayPaid,
        prefix: "৳ ",
        sub: `Collected on ${dayWord}'s orders`,
        icon: <Package size={18} />,
        tone: "mint",
      },
    ];

    return (
      <div className="kd-dash">
        <Breadcrumb title="Dashboard" parent="Home" />
        <div className="container-fluid">

          {/* Hero: greeting, day switch, and the day's numbers on a clothesline */}
          <section className="kd-dash-hero">
            <div className="kd-dash-hero-top">
              <div>
                <p className="kd-dash-greet">{getGreeting()},</p>
                <h2 className="kd-dash-name">{adminName}</h2>
                <p className="kd-dash-date">{selectedDate}</p>
              </div>
              <div className="kd-seg" role="group" aria-label="Choose day">
                {DAYS.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    className={"kd-seg-btn" + (d.key === selectedDay ? " is-active" : "")}
                    aria-pressed={d.key === selectedDay}
                    onClick={() => this.selectDay(d.key)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="kd-tagline">
              <svg className="kd-tagline-rope" viewBox="0 0 1000 40" preserveAspectRatio="none" aria-hidden="true" focusable="false">
                <path d="M0 8 Q500 56 1000 8" />
              </svg>
              <div className="kd-tags">
                {tags.map((tag, i) => (
                  <div
                    key={tag.label}
                    className={`kd-tag-card kd-tone--${tag.tone}`}
                    style={{ "--drop": `${TAG_DROPS[i]}px`, "--i": i }}
                  >
                    <span className="kd-tag-card-hole" aria-hidden="true" />
                    <div className="kd-tag-card-head">
                      <span className="kd-tag-card-label">{tag.label}</span>
                      <span className="kd-tag-card-icon" aria-hidden="true">{tag.icon}</span>
                    </div>
                    <div className="kd-tag-card-value">
                      {tag.prefix}
                      <CountUp end={tag.value} separator="," duration={1.5} />
                    </div>
                    <div className="kd-tag-card-sub">{tag.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Order journey: a stitched track of sewing-button stops */}
          <section className="kd-panel">
            <header className="kd-panel-head">
              <h3 className="kd-panel-title">Order journey</h3>
              <span className="kd-chip">
                {dayTotal} {dayTotal === 1 ? "order" : "orders"} {dayWord}
              </span>
            </header>
            <ol className="kd-journey">
              {pipeline.map((stage) => {
                const share = pct(stage.count, dayTotal);
                return (
                  <li key={stage.key} className={`kd-journey-stop kd-tone--${stage.tone}`}>
                    <Link to={stage.link} className="kd-journey-link">
                      <span
                        className="kd-journey-button"
                        style={{ "--share": `${share * 3.6}deg` }}
                      >
                        <span className="kd-journey-count">{stage.count}</span>
                      </span>
                      <span className="kd-journey-label">{stage.label}</span>
                      <span className="kd-journey-share">{share}%</span>
                    </Link>
                  </li>
                );
              })}
            </ol>
          </section>

          <div className="kd-dash-grid">

            {/* Recent orders */}
            <section className="kd-panel">
              <header className="kd-panel-head">
                <h3 className="kd-panel-title">{dayTitle} orders</h3>
                <span className="kd-chip">Last 5</span>
                <Link to="/orders/All Orders" className="kd-panel-link">
                  View all <ArrowRight size={14} />
                </Link>
              </header>

              {recentLoading ? (
                <SkeletonRows />
              ) : recentOrders.length === 0 ? (
                <EmptyLine>No orders {dayWord} yet.</EmptyLine>
              ) : (
                <ul className="kd-orders">
                  {recentOrders.map((order) => {
                    const addr = this.getDeliveryAddress(order);
                    const total = this.getOrderTotal(order);
                    const orderDate = order.date ? new Date(Number(order.date)) : null;
                    const items = order.orders || [];
                    const visibleItems = items.slice(0, 3);
                    const extraItems = items.length - visibleItems.length;
                    return (
                      <li key={order.id} className="kd-order">
                        <div className="kd-order-id">
                          <span className="kd-order-tag" title={`#${order.id}`}>#{order.id}</span>
                          {orderDate && (
                            <span className="kd-order-time">
                              {orderDate.toLocaleDateString()} {orderDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                            </span>
                          )}
                        </div>

                        <div className="kd-order-who">
                          {addr ? (
                            <>
                              <strong>{addr.fullName || "—"}</strong>
                              {addr.mobileNo && (
                                <span><Phone size={12} /> {addr.mobileNo}</span>
                              )}
                              <span className="kd-order-addr">
                                <MapPin size={12} />
                                {[addr.address, addr.district, addr.division].filter(Boolean).join(", ")}
                              </span>
                            </>
                          ) : (
                            <span className="kd-muted">No address</span>
                          )}
                        </div>

                        <div className="kd-order-items" title={items.map((it) => `${it.name} ×${it.quantity}`).join("\n")}>
                          <span className="kd-thumbs">
                            {visibleItems.map((item, i) =>
                              item.image ? (
                                <img
                                  key={i}
                                  src={item.image}
                                  alt=""
                                  referrerPolicy="no-referrer"
                                  onError={(e) => { e.target.style.visibility = "hidden"; }}
                                />
                              ) : (
                                <span key={i} className="kd-thumb-blank" />
                              )
                            )}
                            {extraItems > 0 && <span className="kd-thumb-more">+{extraItems}</span>}
                          </span>
                          <span className="kd-muted">
                            {items.length} {items.length === 1 ? "item" : "items"}
                          </span>
                        </div>

                        <div className="kd-order-total">৳ {total.toLocaleString()}</div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Bestsellers */}
            <section className="kd-panel">
              <header className="kd-panel-head">
                <h3 className="kd-panel-title">Bestsellers</h3>
                <Link to="/products/physical/restock-dashboard" className="kd-panel-link">
                  Full report <ArrowRight size={14} />
                </Link>
              </header>

              {enrichedLoading ? (
                <SkeletonRows count={5} />
              ) : enriched.length === 0 ? (
                <EmptyLine>
                  No sales stats yet.{" "}
                  <Link to="/products/physical/restock-dashboard">Build them from history</Link>
                </EmptyLine>
              ) : (
                <ol className="kd-best">
                  {enriched.map((p, i) => {
                    const isOut = p.inventory <= 0;
                    const isLow = p.inventory > 0 && p.inventory <= LOW_STOCK_THRESHOLD;
                    return (
                      <li key={p.productId} className="kd-best-row">
                        <span className={"kd-rank" + (i < 3 ? ` kd-rank--${i + 1}` : "")}>{i + 1}</span>
                        {p.pictures && p.pictures[0] ? (
                          <img className="kd-best-img" src={p.pictures[0]} alt="" />
                        ) : (
                          <span className="kd-best-img kd-thumb-blank" />
                        )}
                        <div className="kd-best-main">
                          <span className="kd-best-name">{p.name}</span>
                          <span className="kd-best-bar" aria-hidden="true">
                            <span style={{ width: `${pct(p.totalSold || 0, topSold)}%` }} />
                          </span>
                        </div>
                        <div className="kd-best-nums">
                          <strong>{p.totalSold > 0 ? `${p.totalSold.toLocaleString()} sold` : "—"}</strong>
                          <span className={`badge ${isOut ? "badge-danger" : isLow ? "badge-warning" : "badge-success"}`}>
                            {isOut ? "Out of stock" : isLow ? `Low: ${p.inventory} left` : `${p.inventory.toLocaleString()} in stock`}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </section>
          </div>

          {/* Running low */}
          {alertItems.length > 0 && (
            <section className="kd-panel kd-panel--alert">
              <header className="kd-panel-head">
                <span className="sb-tile sb-tile--peach" aria-hidden="true"><AlertTriangle size={16} /></span>
                <h3 className="kd-panel-title">Running low</h3>
                <span className="kd-chip kd-chip--peach">{outOfStock.length + lowStock.length} items</span>
                <Link to="/products/physical/restock-dashboard" className="kd-panel-link">
                  Restock dashboard <ArrowRight size={14} />
                </Link>
              </header>
              <div className="kd-low">
                {alertItems.map((p) => {
                  const inv = getInventory(p);
                  const isOut = inv === 0;
                  return (
                    <Link
                      key={p.id}
                      to={`/products/physical/add-product/${p.id}`}
                      className={"kd-low-card" + (isOut ? " is-out" : "")}
                    >
                      {p.pictures && p.pictures[0] ? (
                        <img src={p.pictures[0]} alt="" />
                      ) : (
                        <span className="kd-thumb-blank" />
                      )}
                      <span className="kd-low-main">
                        <span className="kd-low-name">{p.name}</span>
                        <span className="kd-low-meter" aria-hidden="true">
                          <span style={{ width: `${pct(inv, LOW_STOCK_THRESHOLD)}%` }} />
                        </span>
                        <span className="kd-low-state">{isOut ? "Out of stock" : `${inv} left`}</span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* Delivered: a small celebration */}
          {deliveredDay > 0 && (
            <section className="kd-cheer">
              <span className="kd-cheer-icon" aria-hidden="true"><CheckCircle size={20} /></span>
              <p>
                <strong>{deliveredDay} {deliveredDay === 1 ? "order" : "orders"} delivered</strong>
                <span>That's {pct(deliveredDay, dayTotal)}% of {dayWord}'s orders.</span>
              </p>
              <svg className="kd-cheer-art" viewBox="-40 -6 120 76" aria-hidden="true" focusable="false">
                <Tee color="#FFFFFF" />
                <g transform="translate(48 2) scale(0.8)"><Sock color="#FFFFFF" /></g>
              </svg>
            </section>
          )}

        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({

  allPayments:  state.payments.payments,
  allAdmins:    state.admins.admins,
  allProducts:  state.products.products,
  currentAdmin: state.admins.currentAdmin,
  productStats: state.products.productStats || [],
});

export default connect(mapStateToProps, { getAllProductStatsRedux })(Dashboard);
